/** Iter 113 — Extras editor for manual bookings: catalog extras with qty,
 * price override and free/included flag, plus custom extras. State lives in
 * the parent as a rich list [{id,name,price,pricing,qty,free,custom}]. */
import { useState } from "react";
import { Input } from "@/components/ui/input";
import Money from "@/components/Money";
import { Sparkles, Plus, Trash2 } from "lucide-react";
import { extraLineTotal, num } from "@/lib/manualPricing";

const P_LABEL = { per_day: "/day", per_booking: "/booking", per_unit: "/unit" };

export function ManualBookingExtrasEditor({ catalog, extras, setExtras, days }) {
  const [custom, setCustom] = useState({ name: "", price: "", pricing: "per_booking" });

  const find = (id) => extras.find((e) => e.id === id && !e.custom);
  const toggle = (a) => {
    const cur = find(a.id);
    if (cur) setExtras((xs) => xs.filter((e) => e !== cur));
    else setExtras((xs) => [...xs, { id: a.id, name: a.name, price: a.price, pricing: a.pricing || a.unit || "per_day", qty: 1, free: false, custom: false, catalogPrice: a.price }]);
  };
  const patch = (target, p) => setExtras((xs) => xs.map((e) => (e === target ? { ...e, ...p } : e)));

  const addCustom = () => {
    if (!custom.name.trim()) return;
    setExtras((xs) => [...xs, { id: null, name: custom.name.trim(), price: num(custom.price) ?? 0, pricing: custom.pricing, qty: 1, free: false, custom: true }]);
    setCustom({ name: "", price: "", pricing: "per_booking" });
  };

  const customs = extras.filter((e) => e.custom);

  return (
    <div className="rounded-xl border border-border p-3" data-testid="manual-extras-editor">
      <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-2 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" />Extras</div>
      <div className="space-y-1.5">
        {catalog.map((a) => {
          const sel = find(a.id);
          return (
            <div key={a.id} className={`rounded-lg border px-3 py-2 transition ${sel ? "border-blue-500 bg-blue-50/40" : "border-border hover:bg-neutral-50"}`} data-testid={`manual-addon-${a.id}`}>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={!!sel} onChange={() => toggle(a)} className="h-4 w-4 accent-blue-600 shrink-0 cursor-pointer" data-testid={`manual-addon-check-${a.id}`} />
                <span className="flex-1 text-sm font-medium leading-tight">{a.name}</span>
                <span className="text-xs text-neutral-500 tabular-nums shrink-0">
                  <Money amount={a.price} />{P_LABEL[a.pricing || a.unit] || "/booking"}
                </span>
              </div>
              {sel && (
                <div className="flex items-center gap-2 mt-1.5 pl-6 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <button type="button" onClick={() => (sel.qty > 1 ? patch(sel, { qty: sel.qty - 1 }) : toggle(a))} className="h-6 w-6 rounded-full border border-border text-sm font-bold leading-none hover:bg-neutral-100" data-testid={`manual-addon-minus-${a.id}`}>−</button>
                    <span className="w-5 text-center text-sm font-bold tabular-nums" data-testid={`manual-addon-qty-${a.id}`}>{sel.qty}</span>
                    <button type="button" onClick={() => patch(sel, { qty: Math.min(9, sel.qty + 1) })} className="h-6 w-6 rounded-full border border-border text-sm font-bold leading-none hover:bg-neutral-100" data-testid={`manual-addon-plus-${a.id}`}>+</button>
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                    €<Input type="number" inputMode="decimal" value={sel.price} disabled={sel.free}
                      onChange={(e) => patch(sel, { price: e.target.value })}
                      className={`h-7 w-20 rounded-lg text-xs ${!sel.free && num(sel.price) !== num(sel.catalogPrice) ? "border-amber-400 bg-amber-50/40" : ""}`}
                      data-testid={`manual-addon-price-${a.id}`} />
                  </span>
                  <label className="inline-flex items-center gap-1 text-xs text-neutral-600 cursor-pointer select-none">
                    <input type="checkbox" checked={sel.free} onChange={(e) => patch(sel, { free: e.target.checked })} className="h-3.5 w-3.5 accent-emerald-600" data-testid={`manual-addon-free-${a.id}`} />
                    Free / included
                  </label>
                  <span className="ml-auto text-xs font-bold tabular-nums" data-testid={`manual-addon-total-${a.id}`}>
                    {sel.free ? "€0.00" : <Money amount={extraLineTotal(sel, days)} />}
                  </span>
                </div>
              )}
            </div>
          );
        })}

        {customs.map((e, i) => (
          <div key={`c-${i}`} className="rounded-lg border border-violet-300 bg-violet-50/40 px-3 py-2 flex items-center gap-2 flex-wrap" data-testid={`manual-custom-extra-${i}`}>
            <span className="text-sm font-medium">{e.name} <span className="text-[10px] uppercase text-violet-600 font-bold">custom</span></span>
            <span className="inline-flex items-center gap-1 text-xs">
              €<Input type="number" value={e.price} disabled={e.free} onChange={(ev) => patch(e, { price: ev.target.value })} className="h-7 w-20 rounded-lg text-xs" />
            </span>
            <select value={e.pricing} onChange={(ev) => patch(e, { pricing: ev.target.value })} className="h-7 rounded-lg border border-border text-xs px-1">
              <option value="per_day">per day</option><option value="per_booking">per booking</option><option value="per_unit">per unit</option>
            </select>
            <span className="inline-flex items-center gap-1">
              <button type="button" onClick={() => patch(e, { qty: Math.max(1, e.qty - 1) })} className="h-6 w-6 rounded-full border border-border text-sm font-bold">−</button>
              <span className="w-5 text-center text-sm font-bold">{e.qty}</span>
              <button type="button" onClick={() => patch(e, { qty: Math.min(9, e.qty + 1) })} className="h-6 w-6 rounded-full border border-border text-sm font-bold">+</button>
            </span>
            <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
              <input type="checkbox" checked={e.free} onChange={(ev) => patch(e, { free: ev.target.checked })} className="h-3.5 w-3.5 accent-emerald-600" /> Free
            </label>
            <span className="ml-auto text-xs font-bold tabular-nums">{e.free ? "€0.00" : <Money amount={extraLineTotal(e, days)} />}</span>
            <button type="button" onClick={() => setExtras((xs) => xs.filter((x) => x !== e))} className="text-rose-500" data-testid={`manual-custom-extra-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}

        <div className="flex gap-1.5 items-center pt-1" data-testid="manual-custom-extra-adder">
          <Input value={custom.name} onChange={(e) => setCustom((c) => ({ ...c, name: e.target.value }))} placeholder="Custom extra (e.g. Ski rack)" className="h-8 rounded-lg text-sm flex-1" data-testid="custom-extra-name" />
          <Input type="number" value={custom.price} onChange={(e) => setCustom((c) => ({ ...c, price: e.target.value }))} placeholder="€" className="h-8 rounded-lg text-sm w-20" data-testid="custom-extra-price" />
          <select value={custom.pricing} onChange={(e) => setCustom((c) => ({ ...c, pricing: e.target.value }))} className="h-8 rounded-lg border border-border text-xs px-1" data-testid="custom-extra-pricing">
            <option value="per_day">per day</option><option value="per_booking">per booking</option><option value="per_unit">per unit</option>
          </select>
          <button type="button" onClick={addCustom} className="h-8 px-2.5 rounded-lg bg-blue-600 text-white text-xs font-bold inline-flex items-center gap-1" data-testid="custom-extra-add">
            <Plus className="h-3 w-3" /> Add
          </button>
        </div>
      </div>
    </div>
  );
}

export default ManualBookingExtrasEditor;
